import { motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, ArrowRight, ExternalLink } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { useLanguage } from "./i18n";
import { copy, projects } from "./siteContent";

export default function ProjectPage() {
  const { slug } = useParams();
  const { language } = useLanguage();
  const reduceMotion = useReducedMotion();
  const t = copy[language];
  const projectIndex = projects.findIndex((item) => item.slug === slug);
  const project = projects[projectIndex];

  if (!project) {
    return (
      <main className="not-found page-shell">
        <p className="section-label">404</p>
        <h1>{t.detail.notFound}</h1>
        <p>{t.detail.notFoundBody}</p>
        <Link className="button button-dark" to="/">{t.detail.home}<ArrowRight size={18} /></Link>
      </main>
    );
  }

  const content = project.content[language];
  const nextProject = projects[(projectIndex + 1) % projects.length];
  const nextContent = nextProject.content[language];
  const reveal = {
    initial: reduceMotion ? false : { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.16 },
    transition: { duration: 0.7, ease: [0.2, 0.8, 0.2, 1] },
  };

  return (
    <main className="project-page">
      <section className="project-hero">
        <div className="page-shell">
          <Link className="back-link" to="/#projects"><ArrowLeft size={17} />{t.detail.back}</Link>
          <motion.div
            className="project-title-grid"
            initial={reduceMotion ? false : { opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
          >
            <div>
              <p className="eyebrow"><span />{content.category}</p>
              <h1>{content.title}</h1>
            </div>
            <p>{content.summary}</p>
          </motion.div>
          <motion.div
            className={`project-hero-media ${project.type === "app" ? "project-hero-media-app" : ""}`}
            initial={reduceMotion ? false : { opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.1 }}
          >
            <img src={project.image} alt={`${content.title} — ${content.cardTitle}`} />
            <div className="project-hero-caption"><span>PK / {String(projectIndex + 1).padStart(2, "0")}</span><span>{content.category}</span></div>
          </motion.div>
        </div>
      </section>

      <section className="project-overview">
        <div className="page-shell overview-grid">
          <motion.div {...reveal}>
            <p className="section-label">{t.detail.overview}</p>
            <p className="overview-lead">{content.intro}</p>
          </motion.div>
          <motion.div className="project-facts" {...reveal}>
            {content.facts.map(([label, value]) => (
              <div key={label}><span>{label}</span><strong>{value}</strong></div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="project-story">
        <div className="page-shell story-shell">
          <p className="section-label">{t.detail.focus}</p>
          <div className="story-grid">
            {content.sections.map(([title, body], index) => (
              <motion.article key={title} className="story-card" {...reveal} transition={{ ...reveal.transition, delay: index * 0.08 }}>
                <span>0{index + 1}</span>
                <h2>{title}</h2>
                <p>{body}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {project.gallery.length > 0 && (
        <section className="project-gallery">
          <div className="page-shell">
            <motion.div className="gallery-heading" {...reveal}>
              <p className="section-label">{t.detail.gallery}</p>
              <span>{String(project.gallery.length).padStart(2, "0")}</span>
            </motion.div>
            <div className={`gallery-grid gallery-count-${project.gallery.length}`}>
              {project.gallery.map((image, index) => (
                <motion.figure key={image} {...reveal} transition={{ ...reveal.transition, delay: Math.min(index * 0.08, 0.18) }}>
                  <img src={image} alt={content.imageAlts[index] || `${content.title} ${index + 1}`} loading="lazy" />
                  <figcaption>{String(index + 1).padStart(2, "0")} / {String(project.gallery.length).padStart(2, "0")}</figcaption>
                </motion.figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {(project.externalUrl || project.graphUrl) && (
        <section className="project-links">
          <div className="page-shell project-links-inner">
            <p>{content.title}</p>
            <div>
              {project.externalUrl && (
                <a className="button button-accent" href={project.externalUrl} target="_blank" rel="noreferrer">
                  {t.detail.store}<ExternalLink size={17} />
                </a>
              )}
              {project.graphUrl && (
                <a className="button button-accent" href={project.graphUrl} target="_blank" rel="noreferrer">
                  {t.detail.viewGraph}<ExternalLink size={17} />
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      <section className="next-project-section">
        <Link className="next-project page-shell" to={`/projects/${nextProject.slug}`}>
          <div><p className="section-label">{t.detail.next}</p><h2>{nextContent.title}</h2></div>
          <span><ArrowRight size={30} /></span>
        </Link>
      </section>
    </main>
  );
}
